import { useEffect, useMemo, useState } from 'react';
import { useController, __INTERNAL__, ExpiryStatus } from '@data-client/react';

import { ApiError } from '../classes/api-error';
import type { FetchWatcher } from '../classes/api';
import { isDataClientFetchWatcher } from '../classes/api';

// Subscribes the calling component to the Data Client store and returns the
// just-committed state. This is the same primitive `useSuspense`/`useCache`
// use internally — the public API doesn't re-export it, so we reach into
// `__INTERNAL__`.
const { useCacheState } = __INTERNAL__;

// Minimal view of the controller for resolving a read against an EXPLICIT
// state. `controller.getResponseMeta(endpoint, ...args, state)` is a real
// (documented) signature, but the public TS type only models the no-state
// form, so we read through this cast.
type MetaController = {
  getError: (endpoint: unknown, ...rest: unknown[]) => unknown;
  getResponseMeta: (
    endpoint: unknown,
    ...rest: unknown[]
  ) => { data: unknown; expiryStatus: ExpiryStatus; expiresAt: number };
};

export type UnwrapFetchWatcherArray<T> = {
  [K in keyof T]: T[K] extends FetchWatcher<infer O> | undefined
    ? // If it could be FetchWatcher or undefined
      undefined extends T[K]
      ? O | undefined
      : O
    : // If it is strictly FetchWatcher (not undefined)
      T[K] extends FetchWatcher<infer O>
      ? O
      : T[K];
};

/**
 * Variadic read hook. Watchers come in two flavors and the hook handles
 * both transparently:
 *
 * 1. **Legacy** (`FetchWatcher` from `EndpointCache`): the watcher
 *    holds its own resolved/error/data state and exposes
 *    `hasResolved` / `getData` / `hasError` / `getError` / `listen`.
 *    The hook subscribes to `listen` for re-render notifications and
 *    throws `Promise.all([toPromise()])` if any aren't ready yet.
 *    This is exactly what `useApiWithErrors` did before the engine
 *    swap — call sites that haven't opted into Data Client schemas
 *    keep their current behavior.
 *
 * 2. **Data Client** (descriptor with `__endpoint` / `__args`):
 *    resolved against the committed store every render, but only
 *    refetched when the endpoint's cache identity changes (see the
 *    `dataClientPromises` memo). A pending fetch is thrown to suspend;
 *    otherwise the cached value is returned synchronously.
 *
 * Mixed arrays (some legacy, some Data Client) are supported — each
 * watcher is handled per its own shape.
 *
 * Thrown `ApiError` instances are caught and returned inline so callers
 * can inspect them with `instanceof ApiError`. `useApi` (the stricter
 * variant) re-throws those so they bubble to the ErrorBoundary.
 */
export const useApiWithErrors = <
  T extends ReadonlyArray<FetchWatcher | undefined | null>,
>(
  ...watchers: T
): UnwrapFetchWatcherArray<T> => {
  // Force a re-render whenever any legacy watcher emits.
  const [, setFetchData] = useState(new Date());
  useEffect(() => {
    const subscriptions = watchers.map(watcher => {
      if (!watcher || isDataClientFetchWatcher(watcher)) return;
      return watcher.listen(() => {
        setFetchData(new Date());
      });
    });
    return () => {
      subscriptions.forEach(sub => sub?.unsubscribe());
    };
  }, [...watchers, setFetchData]);

  // The Data Client controller is read unconditionally (Rules of Hooks).
  // Apps mount <DataProvider> at the root regardless of whether they
  // use schemas yet; the controller is consulted only for Data
  // Client-shaped watchers below.
  const controller = useController();

  // Subscribe to Data Client store mutations AND capture the fresh, just-
  // committed state. Reads below MUST resolve against THIS `cacheState`, not
  // `controller.getState()`: `useEnhancedReducer` (inside `<DataProvider>`)
  // only writes its `stateRef` — what `controller.getState()` reads — in a
  // `useEffect`, i.e. a tick AFTER the render the store change triggers. So on
  // the re-render fired by a mutation's commit (e.g. a create splicing into a
  // cached list, or an `invalidate`), `controller.getState()` still returns the
  // PRE-commit state and the component renders stale, with no further re-render
  // — the UI only updates on an unrelated re-render or reload. `useCacheState()`
  // returns the post-commit state synchronously, which is what `useSuspense`
  // consumers resolve against.
  const cacheState = useCacheState();

  const isThenable = (v: unknown): v is PromiseLike<unknown> =>
    v !== null &&
    typeof v === 'object' &&
    typeof (v as { then?: unknown }).then === 'function';

  // Resolve each data-client watcher against the just-committed `cacheState`.
  // This stays per-render on purpose: re-denormalizing every render is what
  // lets a write that merges into a shared entity surface in an
  // already-mounted read without anyone refetching.
  const metas = watchers.map(w => {
    if (!w || !isDataClientFetchWatcher(w)) return null;
    const args = w.__args as Parameters<typeof w.__endpoint>;
    const meta = controller as unknown as MetaController;
    const { data, expiryStatus, expiresAt } = meta.getResponseMeta(
      w.__endpoint,
      ...args,
      cacheState
    );
    return {
      data,
      error: meta.getError(w.__endpoint, ...args, cacheState),
      expiresAt,
      expiryStatus,
      key: w.__endpoint.key(...args),
    };
  });

  // Scalar dep for the fetch memo below. `Api.read()` hands back a fresh object
  // literal on every call, so `watchers` churns identity every render and can
  // never be a dep — but `__endpoint` is memoized in `Api.endpointInstances`, so
  // `key()` is stable across renders for the same request.
  const { lastReset } = cacheState as unknown as { lastReset: number };
  const fetchDep = `${metas
    .map(m =>
      m
        ? `${m.key}|${m.expiresAt}|${
            m.expiryStatus === ExpiryStatus.Invalid ? 1 : 0
          }`
        : ''
    )
    .join('~')}~${lastReset}`;

  // Kick off every stale data-client fetch BEFORE deciding whether to suspend,
  // so all N reads are issued in parallel rather than suspending on the first
  // pending one (which would serialize the round-trips).
  //
  // The decision is memoized on `fetchDep`, deliberately NOT on `cacheState`.
  // `useCacheState` subscribes this component to the WHOLE store, so any commit
  // anywhere — a polling read elsewhere in the app, an unrelated PATCH —
  // re-renders every consumer. Re-evaluating `Date.now() <= expiresAt` on each
  // of those renders turns a mount-time staleness check into a clock-driven
  // one, and a single 10s poll then drags every mounted read past its TTL along
  // with it. Pinning the decision to `expiresAt` means elapsed time alone can
  // never fire a request, while mounting, changing args, invalidating, or
  // resetting the store still do. Same contract as `useSuspense` upstream.
  const dataClientPromises = useMemo(() => {
    // A `forEach` that pushes, rather than a `.map` returning the fetch: the
    // `promise-function-async` lint fixer rewrites a promise-returning callback
    // to `async`, which would make EVERY slot a thenable instead of `undefined`
    // and suspend reads that are perfectly fresh.
    const pending: Array<PromiseLike<unknown> | undefined> = [];
    metas.forEach((m, i) => {
      const w = watchers[i];
      const forceFetch = m?.expiryStatus === ExpiryStatus.Invalid;
      if (
        !m ||
        !w ||
        !isDataClientFetchWatcher(w) ||
        m.error !== undefined ||
        (Date.now() <= m.expiresAt && !forceFetch)
      ) {
        pending.push(undefined);
        return;
      }
      // Swallow the rejection: a failed fetch surfaces through `getError` on
      // the re-render its error commit triggers, so letting it reject would
      // double-report and leave an unhandled rejection behind whenever this
      // promise isn't the one thrown.
      pending.push(
        controller
          .fetch(w.__endpoint, ...(w.__args as Parameters<typeof w.__endpoint>))
          .catch(() => {})
      );
    });
    return pending;
    // Keyed on `fetchDep` alone — adding `metas` / `watchers` / `cacheState`
    // would reintroduce the per-render evaluation this memo exists to prevent.
  }, [fetchDep]);

  const dataClientResults = metas.map((m, i) => {
    if (!m) return;
    // A committed error ends the cycle: without this the entry would sit at its
    // (short) error expiry with no promise to suspend on and no data to render.
    if (m.error !== undefined) return m.error;

    const promise = dataClientPromises[i];
    if (promise === undefined) return m.data;

    // `Valid` means the cache still holds a usable value that has merely gone
    // stale, so revalidate behind it and keep rendering — dropping the whole
    // subtree into a fallback to refresh data the user is already looking at is
    // what makes a routine background refresh read as a page reload. Only a
    // hard invalid or a never-fetched entry has nothing to show and suspends.
    return m.expiryStatus === ExpiryStatus.Valid ? m.data : promise;
  });

  // If anything is still pending — legacy `hasResolved()` false OR a
  // data-client entry that just fired a fetch — suspend on the union. Both
  // kinds are already in flight, so Suspense resumes once the slowest of them
  // settles, not in N sequential steps.
  const legacyPending: Array<PromiseLike<unknown>> = [];
  for (const w of watchers) {
    if (!w) continue;
    if (!isDataClientFetchWatcher(w) && !w.hasResolved()) {
      legacyPending.push(w.toPromise());
    }
  }
  const dataClientPending = dataClientResults.filter(v => isThenable(v));
  if (legacyPending.length + dataClientPending.length > 0) {
    // Suspense API requires throwing a thenable. Cast as Error to
    // satisfy @typescript-eslint/only-throw-error — at runtime React
    // detects the thenable shape, not the static type.
    throw Promise.all([
      ...legacyPending,
      ...dataClientPending,
    ]) as unknown as Error;
  }

  return watchers.map((w, i) => {
    if (!w) return;
    if (!isDataClientFetchWatcher(w)) {
      // Legacy: read state synchronously (we already know it's resolved).
      return w.hasError() ? w.getError() : w.getData();
    }
    // Data Client: no thenable is left in `dataClientPending` by here, so
    // this is a sync value. Return ApiError inline for callers that inspect
    // errors with `instanceof ApiError`.
    const result = dataClientResults[i];
    return result instanceof ApiError ? result : result;
  }) as UnwrapFetchWatcherArray<T>;
};
